import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { parseError } from "../utils/utils";
import { useSelector } from "react-redux";
import { selectUserInfo } from "@redux/slices/authSlice";
import { Link } from "react-router-dom";
import ModalConfirm from "@components/misc/ModalConfirm";
import ScrollContainer from "@components/misc/ScrollContainer";
import { Pencil, PauseCircle, PlayCircle, Trash, Eye, EyeOff } from "lucide-react";
import config from "../config";

const API_BASE_URL = config.API_BASE_URL;

const CompaniesList = () => {
  const [companies, setCompanies] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [companyToDelete, setCompanyToDelete] = useState(null);
  const [editCompanyId, setEditCompanyId] = useState(null);
  const [, setIsLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const pageSize = 10;

  const [formData, setFormData] = useState({
    name: "",
    domain: "",
    is_active: true,
    admin_email: "",
    admin_password: "",
  });

  const userInfo = useSelector(selectUserInfo);
  const token = localStorage.getItem("accessToken");

  useEffect(() => {
    if (userInfo?.role?.toLowerCase() === "superadmin") {
      fetchCompanies();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo]);



  const fetchCompanies = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/users/companies/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCompanies(response.data);
    } catch (error) {
      toast.error(parseError(error));
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e, isEdit = false) => {
    const { name, value, type, checked } = e.target;
    let newValue = type === "checkbox" ? checked : value;
    let newError = "";

    if (name === "admin_password" && typeof newValue === "string") {
      // Spaces allowed in state for now
    }

    // Real-time validation
    if (name === "name") {
      if (!newValue.trim()) {
        newError = "Company name is required";
      } else if (/[^A-Za-z0-9 ]/.test(newValue)) {
        newError = "No special characters are allowed";
      } else if (newValue.length < 2) {
        newError = "Min 2 characters required";
      }
    }

    if (name === "domain") {
      if (!newValue.trim()) {
        newError = "Domain is required";
      } else if (/https?:\/\//i.test(newValue)) {
        newError = "No http:// or https:// allowed";
      } else if (!/^(?!https?:\/\/)([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/.test(newValue)) {
        newError = "Enter valid domain (e.g., company.com)";
      }
    }

    if (name === "admin_email") {
      if (!newValue.trim()) {
        newError = "Email is required";
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newValue)) {
        newError = "Invalid email address";
      } else {
        const personalDomains = ["@gmail.", "@yahoo.", "@outlook."];
        if (personalDomains.some(d => newValue.toLowerCase().includes(d))) {
          newError = "Personal emails (gmail, yahoo, outlook) are not allowed";
        }
      }
    }

    if (name === "admin_password") {
      if (!newValue) {
        newError = "Password is required";
      } else if (newValue.length < 8) {
        newError = "Minimum 8 characters required";
      } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])/.test(newValue)) {
        newError = "Must include A-Z, a-z, 0-9, and special char";
      } else if (/(.)\1{2,}/.test(newValue)) {
        newError = "No more than 2 same characters continuously allowed";
      }
    }

    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: newError,
    }));
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();

    // Final Validation Check
    const hasErrors = Object.values(errors).some(err => err !== "");
    const hasEmptyFields = !formData.name || !formData.domain || !formData.admin_email || !formData.admin_password;

    if (hasErrors || hasEmptyFields) {
      toast.error("Please fix the validation errors before submitting.");
      return;
    }

    if (/\s/.test(formData.admin_password)) {
      toast.error("Spaces are not allowed in passwords.");
      return;
    }

    try {
      // 1. Create the Company
      const companyRes = await axios.post(
        `${API_BASE_URL}/api/users/companies/`,
        {
          name: formData.name,
          domain: formData.domain,
          is_active: formData.is_active
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const newCompanyId = companyRes.data.id;

      // 2. Create the Master Admin for that Company
      if (formData.admin_email && formData.admin_password) {
        await axios.post(
          `${API_BASE_URL}/api/users/members/add/`,
          {
            email: formData.admin_email,
            password: formData.admin_password.replace(/\s/g, ""),
            password2: formData.admin_password.replace(/\s/g, ""),
            first_name: "Company",
            last_name: "Admin",
            role: "masteradmin",
            active: true,
            company_id: newCompanyId,
            force_password_reset: true
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      toast.success("Company & Admin User created successfully");
      setShowAddForm(false);
      fetchCompanies();
    } catch (error) {
      toast.error(parseError(error));
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();

    // Validations (same pattern as Add)
    const nameRegex = /^[A-Za-z0-9 ]{2,}$/;
    if (!formData.name.trim()) {
      toast.error("Company name is required");
      return;
    }
    if (!nameRegex.test(formData.name)) {
      toast.error("Enter valid company name");
      return;
    }

    const domainRegex = /^(?!https?:\/\/)([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/;
    if (!formData.domain.trim()) {
      toast.error("Domain is required");
      return;
    }
    if (!domainRegex.test(formData.domain)) {
      toast.error("Enter valid domain (e.g., company.com)");
      return;
    }

    try {
      await axios.put(
        `${API_BASE_URL}/api/users/companies/${editCompanyId}/`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Company updated successfully");
      setShowEditForm(false);
      setEditCompanyId(null);
      fetchCompanies();
    } catch (error) {
      toast.error(parseError(error));
    }
  };

  const openEditForm = (company) => {
    setFormData({
      name: company.name,
      domain: company.domain || "",
      is_active: company.is_active,
    });
    setEditCompanyId(company.id);
    setShowEditForm(true);
  };

  const handleDeleteClick = (company) => {
    setCompanyToDelete(company);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(
        `${API_BASE_URL}/api/users/companies/${companyToDelete.id}/`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Company deleted successfully");
      fetchCompanies();
    } catch (error) {
      toast.error(parseError(error));
    } finally {
      setShowDeleteModal(false);
      setCompanyToDelete(null);
    }
  };

  const toggleStatus = async (company) => {
    try {
      await axios.put(
        `${API_BASE_URL}/api/users/companies/${company.id}/`,
        { is_active: !company.is_active },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(
        `Company ${!company.is_active ? "activated" : "deactivated"} successfully`
      );
      fetchCompanies();
    } catch (error) {
      toast.error(parseError(error));
    }
  };

  if (userInfo?.role?.toLowerCase() !== "superadmin") {
    return (
      <div className="flex bg-white min-h-screen text-gray-900 pt-16 justify-center">
        <h2>Access Denied. Superadmin only.</h2>
      </div>
    );
  }

  const isSubsequence = (query, target) => {
    let qIdx = 0;
    let tIdx = 0;
    while (qIdx < query.length && tIdx < target.length) {
      if (query[qIdx] === target[tIdx]) {
        qIdx++;
      }
      tIdx++;
    }
    return qIdx === query.length;
  };

  const filteredCompanies = companies.filter((company) => {
    if (!searchTerm) return true;
    const sQuery = searchTerm.replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
    if (!sQuery) return true;
    const sName = (company.name || "").replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
    const sDomain = (company.domain || "").replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
    return isSubsequence(sQuery, sName) || isSubsequence(sQuery, sDomain);
  });

  const totalPages = Math.max(1, Math.ceil(filteredCompanies.length / pageSize));

  const validCurrentPage = Math.min(currentPage, totalPages);

  const paginatedCompanies = filteredCompanies.slice(
    (validCurrentPage - 1) * pageSize,
    validCurrentPage * pageSize
  );

  return (
    <>
      <div
        className="mt-16 p-12 min-h-screen bg-white"
        id="listing"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <section className="">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-x-3">
                <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-700 via-purple-700 to-indigo-800 bg-clip-text text-transparent tracking-wider">
                  Control Panel
                </h2>
              </div>
              <div className="shrink-0 flex items-center gap-4">
                <div className="relative inline-flex items-center">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    placeholder="Search Company..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="w-[240px] h-10 pl-10 pr-4 text-sm border border-blue-400 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all font-medium text-gray-700 placeholder-gray-400 bg-white"
                  />
                </div>
                <Link
                  to="/integrations/listing"
                  className="flex items-center justify-center bg-[#f0f7ff] text-[#1e3a8a] border border-[#cce3ff] px-5 py-2 rounded-full transition-all duration-200 font-bold shadow-sm active:scale-95 text-sm whitespace-nowrap hover:bg-[#e0efff] hover:border-[#b8d9ff]"
                >
                  Integrations
                </Link>
                <Link
                  to="/agent"
                  className="flex items-center justify-center bg-[#f0f7ff] text-[#1e3a8a] border border-[#cce3ff] px-5 py-2 rounded-full transition-all duration-200 font-bold shadow-sm active:scale-95 text-sm whitespace-nowrap hover:bg-[#e0efff] hover:border-[#b8d9ff]"
                >
                  Launch Agent
                </Link>
                <div className="h-8 w-px bg-gray-200 mx-1"></div>
                <button
                  onClick={() => {
                    setFormData({ name: "", domain: "", is_active: true });
                    setShowAddForm(true);
                  }}
                  className="flex items-center justify-center gap-x-2 rounded-full bg-blue-600 px-6 py-2.5 text-sm font-bold text-white transition-all duration-200 hover:bg-blue-700 shadow-lg hover:shadow-blue-200 active:scale-95"
                >
                  Add New Company
                </button>
              </div>
            </div>

            <div className="mt-6 flex flex-col">
              <ScrollContainer className="rounded-lg shadow-sm">
                <div className="inline-block min-w-full align-middle">
                  <div className="overflow-hidden border border-blue-400 shadow-sm bg-white md:rounded-lg">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th scope="col" className="px-4 py-4 text-left text-sm font-bold text-gray-900 tracking-wider">
                            Name
                          </th>
                          <th scope="col" className="px-4 py-4 text-left text-sm font-bold text-gray-900 tracking-wider">
                            Domain
                          </th>
                          <th scope="col" className="px-4 py-4 text-left text-sm font-bold text-gray-900 tracking-wider">
                            Admin Email
                          </th>
                          <th scope="col" className="px-4 py-4 text-left text-sm font-bold text-gray-900 tracking-wider">
                            Status
                          </th>
                          <th scope="col" className="px-4 py-4 text-left text-sm font-bold text-gray-900 tracking-wider">
                            Created At
                          </th>
                          <th scope="col" className="px-4 py-4 text-left text-sm font-bold text-gray-900 tracking-wider w-[120px]">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200 border-t border-gray-200">
                        {companies.length > 0 ? (
                          paginatedCompanies.map((company) => (
                            <tr key={company.id}>
                              <td className="whitespace-nowrap px-4 py-3 text-base text-gray-900 font-medium">
                                {company.name}
                              </td>
                              <td className="whitespace-nowrap px-4 py-3 text-base text-gray-900 font-medium">
                                {company.domain || "N/A"}
                              </td>
                              <td className="whitespace-nowrap px-4 py-3 text-base text-gray-900 font-medium lowercase">
                                {company.admin_email || "N/A"}
                              </td>
                              <td className="whitespace-nowrap px-4 py-3 text-base text-gray-900 font-medium">
                                {company.is_active ? (
                                  <span className="inline-flex items-center gap-x-1.5 w-28 justify-center rounded-full bg-green-100 px-2.5 py-1 text-xs font-bold text-green-700 border border-green-200 shadow-sm">
                                    Active
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-x-1.5 w-28 justify-center rounded-full bg-red-100 px-2.5 py-1 text-xs font-bold text-red-700 border border-red-200 shadow-sm">
                                    Deactivated
                                  </span>
                                )}
                              </td>
                              <td className="whitespace-nowrap px-4 py-3 text-base text-gray-900 font-medium">
                                {new Date(company.created_at).toLocaleDateString()}
                              </td>
                              <td className="whitespace-nowrap px-4 py-3 text-base text-gray-900 font-medium">
                                <div className="flex items-center justify-start gap-2">
                                  <div className="group relative">
                                    <button
                                      onClick={() => openEditForm(company)}
                                      aria-label="Edit company"
                                      className="rounded-full p-2 text-blue-500 transition-all duration-200 hover:bg-[#f0f7ff] hover:text-[#1e3a8a] active:scale-90"
                                    >
                                      <Pencil size={20} />
                                    </button>
                                    <span className="pointer-events-none absolute -top-10 right-0 z-20 whitespace-nowrap rounded-md bg-gray-900 px-2 py-1 text-xs font-semibold text-white opacity-0 shadow-lg transition-opacity duration-200 group-hover:opacity-100">
                                      Edit company details
                                    </span>
                                  </div>
                                  <div className="group relative">
                                    <button
                                      onClick={() => toggleStatus(company)}
                                      aria-label={company.is_active ? "Deactivate company" : "Activate company"}
                                      className={`rounded-full p-2 text-indigo-500 transition-all duration-200 hover:bg-indigo-50 hover:text-indigo-700 active:scale-90`}
                                    >
                                      {company.is_active ? <PauseCircle size={20} /> : <PlayCircle size={20} />}
                                    </button>
                                    <span className="pointer-events-none absolute -top-10 right-0 z-20 whitespace-nowrap rounded-md bg-gray-900 px-2 py-1 text-xs font-semibold text-white opacity-0 shadow-lg transition-opacity duration-200 group-hover:opacity-100">
                                      {company.is_active ? "Deactivate company" : "Activate company"}
                                    </span>
                                  </div>
                                  {company.name?.toUpperCase() !== "Z-NINTH" && (
                                    <div className="group relative">
                                      <button
                                        onClick={() => handleDeleteClick(company)}
                                        aria-label="Delete company"
                                        className="rounded-full p-2 text-red-500 transition-all duration-200 hover:bg-red-50 hover:text-red-600 active:scale-90"
                                      >
                                        <Trash size={20} />
                                      </button>
                                      <span className="pointer-events-none absolute -top-10 right-0 z-20 whitespace-nowrap rounded-md bg-gray-900 px-2 py-1 text-xs font-semibold text-white opacity-0 shadow-lg transition-opacity duration-200 group-hover:opacity-100">
                                        Delete company
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="5" className="whitespace-nowrap px-4 py-8 text-sm text-gray-500 text-center">
                              No companies found.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </ScrollContainer>
              {companies.length > 0 && (
                <div className="mt-10 flex items-center justify-center px-4">
                  <div className="flex select-none items-center gap-x-4">
                    <button
                      onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                      disabled={currentPage === 1}
                      className={`flex h-10 min-w-[120px] items-center justify-center gap-1 px-4 shrink-0 rounded-full font-bold text-sm shadow-sm active:scale-95 transition-all duration-300 ${currentPage === 1
                        ? "bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed shadow-none"
                        : "bg-[#f0f7ff] text-[#1e3a8a] border border-[#cce3ff] hover:bg-[#e0efff] hover:border-[#b8d9ff] hover:shadow-md"
                        }`}
                    >
                      <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                        <path d="M15 19l-7-7 7-7" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      Previous
                    </button>

                    <div className="h-10 w-10 shrink-0 flex items-center justify-center rounded-full border border-blue-700 bg-blue-600 text-sm font-bold text-white shadow-md">
                      {validCurrentPage}
                    </div>

                    <button
                      onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                      disabled={currentPage === totalPages}
                      className={`flex h-10 min-w-[120px] items-center justify-center gap-1 px-4 shrink-0 rounded-full font-bold text-sm shadow-sm active:scale-95 transition-all duration-300 ${currentPage === totalPages
                        ? "bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed shadow-none"
                        : "bg-[#f0f7ff] text-[#1e3a8a] border border-[#cce3ff] hover:bg-[#e0efff] hover:border-[#b8d9ff] hover:shadow-md"
                        }`}
                    >
                      Next
                      <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                        <path d="M9 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </section>        </div>
        {/* Add/Edit Form - Sidebar Style */}
        {(showAddForm || showEditForm) && (
          <div className="fixed top-24 right-0 w-[calc(100vw-1rem)] sm:w-[420px] h-fit max-h-[calc(100vh-120px)] bg-white border border-blue-400 shadow-2xl p-6 overflow-y-auto overflow-x-hidden transition-all duration-300 rounded-l-3xl z-[60]">
            <div className="flex justify-between items-center mb-4 pb-3 border-b border-gray-100">
              <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-600 via-purple-700 to-indigo-800 bg-clip-text text-transparent tracking-wider">
                {showAddForm ? "Add Company" : "Edit Company"}
              </h2>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => {
                    setShowAddForm(false);
                    setShowEditForm(false);
                  }}
                  className="px-5 py-2 rounded-full bg-[#f0f7ff] border border-[#cce3ff] text-sm font-bold text-[#1e3a8a] shadow-sm transition-all duration-200 hover:bg-[#e0efff] hover:border-[#b8d9ff] active:scale-95"
                >
                  Cancel
                </button>
              </div>
            </div>

            <form onSubmit={showAddForm ? handleAddSubmit : handleEditSubmit} className="space-y-3.5">
              <div className="space-y-1">
                <label className="block text-sm font-bold text-gray-700 tracking-tight ml-1">
                  Company Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  placeholder="Company Name"
                  value={formData.name}
                  onChange={(e) => handleInputChange(e)}
                  required
                  className={`w-full h-11 px-4 bg-white border ${errors.name ? 'border-red-500' : 'border-gray-200'} rounded-xl text-gray-900 shadow-sm placeholder-gray-700 focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all font-medium text-sm`}
                />
                {errors.name && <p className="text-red-500 text-[10px] font-bold mt-1 ml-1 tracking-tight">{errors.name}</p>}
              </div>
              <div className="space-y-1">
                <label className="block text-sm font-bold text-gray-700 tracking-tight ml-1">
                  Domain <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="domain"
                  placeholder="Domain"
                  value={formData.domain}
                  onChange={(e) => handleInputChange(e)}
                  className={`w-full h-11 px-4 bg-white border ${errors.domain ? 'border-red-500' : 'border-gray-200'} rounded-xl text-gray-900 shadow-sm placeholder-gray-700 focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all font-medium text-sm`}
                />
                {errors.domain && <p className="text-red-500 text-[10px] font-bold mt-1 ml-1 tracking-tight">{errors.domain}</p>}
              </div>

              {showAddForm && (
                <>
                  <div className="space-y-1">
                    <label className="block text-sm font-bold text-gray-700 tracking-tight ml-1">
                      Admin Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="admin_email"
                      placeholder="Email"
                      value={formData.admin_email}
                      onChange={(e) => handleInputChange(e)}
                      required={showAddForm}
                      className={`w-full h-11 px-4 bg-white border-2 ${errors.admin_email ? 'border-red-500' : 'border-gray-100'} rounded-xl text-gray-900 shadow-sm placeholder-gray-700 focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all font-medium text-sm`}
                    />
                    {errors.admin_email && <p className="text-red-500 text-[10px] font-bold mt-1 ml-1 tracking-tight">{errors.admin_email}</p>}
                  </div>
                  <div className="space-y-1">
                    <label className="block text-sm font-bold text-gray-700 tracking-tight ml-1">
                      Admin Password <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        name="admin_password"
                        placeholder="Password"
                        value={formData.admin_password}
                        onChange={(e) => handleInputChange(e)}
                        required={showAddForm}
                        className={`w-full h-11 px-4 pr-12 bg-white border-2 ${errors.admin_password ? 'border-red-500' : 'border-gray-100'} rounded-xl text-gray-900 shadow-sm placeholder-gray-700 focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all font-medium text-sm`}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute inset-y-0 right-0 flex items-center pr-4 text-gray-400 hover:text-gray-600 outline-none"
                      >
                        {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                      </button>
                    </div>
                    {errors.admin_password && <p className="text-red-500 text-[10px] font-bold mt-1 ml-1 tracking-tight">{errors.admin_password}</p>}
                  </div>
                </>
              )}

              <div className="flex items-center justify-between pt-4 mt-2">
                <span className="text-gray-900 font-bold tracking-tight">Active Subscription</span>
                <label className="relative inline-flex items-center cursor-pointer scale-110">
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={(e) => handleInputChange(e)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-600 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-500"></div>
                </label>
              </div>

              <div className="pt-4">
                <button
                  type="submit"
                  className="w-full bg-blue-600 text-white px-6 py-3.5 rounded-xl hover:bg-blue-700 transition-all duration-300 font-black text-base tracking-wide shadow-lg hover:shadow-blue-200 active:scale-95"
                >
                  {showAddForm ? "Generate Credentials" : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        )}

        <ModalConfirm
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={confirmDelete}
          title="Delete Company"
          message={`Are you sure you want to delete ${companyToDelete?.name}? This action cannot be undone and will delete all their integrations and data.`}
          confirmText="Delete"
          cancelText="Cancel"
          confirmColor="bg-red-500 hover:bg-red-600"
        />
      </div>
    </>
  );
};

export default CompaniesList;
